/**
 * OrganizatorSerbestKwAsimiPage.jsx — "Stands Above the Free kW": the organizer's list of stands whose Elektrik request is above the kW the fair gives free
 * (backlog #500, 2026-10-19; backend #499; the allowance logic is KwKontrolService, #455/#457).
 *
 * Route /Firma/Organizator/SerbestKwAsimi, opened from the button in the header of the Talepler page (SerbestKwDugmesi). It belongs to the Talepler AREA: the route is
 * gated like Talepler (İzleme or Denetim), and the backend answers the same area guard. The page only reads; it draws no action at any tier.
 *
 * Shows the fair's rule (flat / block / bands), three tiles (stands over, total excess kW, stands that cannot be judged: they have no drawn area, or an area in no
 * band), and a table of the stands, largest excess first. The tiles follow the search and the hall; the third tile switches the table to the stands that cannot be
 * judged, the first one back. The area is the KROKI area (#455), not the m² typed in a declaration. A row opens the stand's request page on its Elektrik card, with a
 * way back here (?geri=). State is in the address (?ara=&salon=&degerlendirilemeyen=1&sayfa=), see Components/Talep/serbestKwAsimi.js.
 *
 * The hall list is the halls endpoint's (SALONLAR tier): a company that has Talepler but not Salonlar gets no hall dropdown instead of an error.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Alert, Badge, Card, CardBody, CardHeader, Col, Container, Input, Progress, Row, Spinner, Table, Pagination, PaginationItem, PaginationLink } from "reactstrap";
import { ArrowLeft, Zap } from "lucide-react";
import { useTranslation } from "react-i18next";
import { usePortal } from "../../Components/Contexts/PortalContext";
import { getOrganizatorSerbestKwAsimi, getOrganizatorSalonlar } from "../../services/portalService";
import {
  adresOku, adresYaz, apiParametreleri, cubukOranlari, enBuyukIstenen, talepSayfasiYolu,
} from "../../Components/Talep/serbestKwAsimi";
import { kwYazi } from "../../Components/Talep/kwKontrol";
import { currentLocaleTag } from "../../i18n";
import i18n from "../../i18n";

const TALEPLER_YOLU = "/Firma/Organizator/Talepler";

const hataMetni = (e) => e?.message || i18n.t("portal:organizator.islemBasarisizOldu");

/** The status of the request on the row (the list holds Bekliyor and Onaylı only). */
const durumRenk = (s) => (s === 1 ? "success" : "warning");

/** One kW / m² number in the page's number format; "—" when the backend sent none. */
const sayi = (n, yerel) => (n === null || n === undefined ? "—" : kwYazi(n, yerel) || "—");

/** Free part (green) + excess part (red) of the request, scaled to the largest request on the page; a row that cannot be judged is one grey part. */
function KwCubugu({ satir, enBuyuk }) {
  const o = cubukOranlari(satir, enBuyuk);
  return (
    <Progress multi style={{ height: 8, minWidth: 90 }} aria-hidden="true" data-testid="kw-cubugu">
      {o.serbest > 0 && <Progress bar color="success" value={o.serbest} />}
      {o.asan > 0 && <Progress bar color="danger" value={o.asan} />}
      {o.belirsiz > 0 && <Progress bar color="secondary" value={o.belirsiz} />}
    </Progress>
  );
}

/** A summary tile; with `onClick` it is a button (the first and the third tile switch the table's list). */
function OzetKutusu({ etiket, deger, renk, secili, onClick, testid }) {
  const icerik = (
    <>
      <div className={`fs-4 fw-semibold lh-1 text-${renk}`}>{deger}</div>
      <div className="small text-muted mt-1">{etiket}</div>
    </>
  );
  return (
    <Card className={`mb-2 h-100 ${secili ? "border-primary" : ""}`} data-testid={testid}>
      {onClick ? (
        <button type="button" className="card-body py-2 text-start bg-transparent border-0" aria-pressed={secili} onClick={onClick}>{icerik}</button>
      ) : (
        <CardBody className="py-2">{icerik}</CardBody>
      )}
    </Card>
  );
}

/** The rule as chips: its mode, then its values (flat kW, the block, or one chip per band). */
function KuralSatiri({ kural, yerel }) {
  const { t } = useTranslation("portal");
  const kw = (n) => kwYazi(n, yerel);
  let degerler = [];
  if (kural.mod === "FLAT") {
    degerler = [t("organizator.kwAsimi.kuralFlat", { kw: kw(kural.serbestKw) })];
  } else if (kural.mod === "BLOCK") {
    degerler = [t("organizator.kwAsimi.kuralBlok", { kw: kw(kural.blokKw), m2: kw(kural.blokM2) })];
  } else if (kural.mod === "BANDS") {
    degerler = (kural.kademeler ?? []).map((k) => {
      const hasMin = k.minM2 !== null && k.minM2 !== undefined;
      const hasMax = k.maxM2 !== null && k.maxM2 !== undefined;
      if (hasMin && hasMax) return t("organizator.kwAsimi.kademeAralik", { min: kw(k.minM2), max: kw(k.maxM2), kw: kw(k.serbestKw) });
      if (hasMin) return t("organizator.kwAsimi.kademeUstu", { min: kw(k.minM2), kw: kw(k.serbestKw) });
      return t("organizator.kwAsimi.kademeAlti", { max: kw(k.maxM2), kw: kw(k.serbestKw) });
    });
  }
  return (
    <div className="d-flex flex-wrap align-items-center gap-1 mb-3" data-testid="kural-satiri">
      <Badge color="primary">{t(`organizator.kwAsimi.mod.${kural.mod}`)}</Badge>
      {degerler.map((d) => <Badge key={d} color="light" className="text-body border">{d}</Badge>)}
    </div>
  );
}

export default function OrganizatorSerbestKwAsimiPage() {
  const { t } = useTranslation("portal");
  const portal = usePortal();
  const fuarId = portal?.fuarId;
  const organizator = portal?.arayuzler?.find((a) => a.rol === "ORGANIZATOR") ?? null;
  const navigate = useNavigate();
  const konum = useLocation();
  const [params, setParams] = useSearchParams();
  const durum = useMemo(() => adresOku(params), [params]);
  const yerel = currentLocaleTag();
  useEffect(() => { document.title = i18n.t("portal:organizator.organizatorPaneliFds"); }, []);

  // ── state in the address: the search text goes there once typing pauses ───────────────────────────
  const [girdi, setGirdi] = useState(durum.ara);
  useEffect(() => {
    if (girdi.trim() === durum.ara) return undefined;
    const zamanlayici = setTimeout(() => {
      setParams((onceki) => adresYaz({ ...adresOku(onceki), ara: girdi, sayfa: 0 }, onceki), { replace: true });
    }, 350);
    return () => clearTimeout(zamanlayici);
  }, [girdi, durum.ara, setParams]);
  // a changed filter starts again from the first page
  const degistir = (yama) => setParams((onceki) => adresYaz({ ...adresOku(onceki), sayfa: 0, ...yama }, onceki), { replace: true });
  const sayfayaGit = (s) => setParams((onceki) => adresYaz({ ...adresOku(onceki), sayfa: s }, onceki), { replace: true });

  // ── the list ───────────────────────────────────────────────────────────────────────────────────────
  const [veri, setVeri] = useState(null);
  const [yukleniyor, setYukleniyor] = useState(false);
  const [hata, setHata] = useState(null);
  const istekAnahtari = JSON.stringify(apiParametreleri(durum));
  const organizatorVar = Boolean(organizator);
  // KURAL: fetch Effect içindeki async fonksiyonda (react-hooks/set-state-in-effect).
  useEffect(() => {
    let ignore = false;
    async function run() {
      setYukleniyor(true);
      setHata(null);
      try {
        const out = await getOrganizatorSerbestKwAsimi(fuarId, JSON.parse(istekAnahtari));
        if (!ignore) setVeri(out);
      } catch (e) {
        if (!ignore) setHata({ mesaj: hataMetni(e), durum: e?.status ?? null });
      } finally {
        if (!ignore) setYukleniyor(false);
      }
    }
    if (fuarId && organizatorVar) run();
    return () => { ignore = true; };
  }, [fuarId, istekAnahtari, organizatorVar]);

  // ── the halls (SALONLAR tier: refused = no dropdown) ───────────────────────────────────────────────
  const [salonSonucu, setSalonSonucu] = useState({ fuarId: null, liste: [] });
  useEffect(() => {
    let ignore = false;
    async function run() {
      try {
        const out = await getOrganizatorSalonlar(fuarId);
        if (!ignore) setSalonSonucu({ fuarId, liste: Array.isArray(out) ? out : [] });
      } catch {
        if (!ignore) setSalonSonucu({ fuarId, liste: [] });
      }
    }
    if (fuarId && organizatorVar) run();
    return () => { ignore = true; };
  }, [fuarId, organizatorVar]);
  const salonlar = salonSonucu.fuarId === fuarId ? salonSonucu.liste : [];

  // ── states that replace the page ───────────────────────────────────────────────────────────────────
  if (!organizator) {
    return (
      <div className="page-content"><Container fluid>
        <Alert color="warning" className="mt-3">{t("organizator.firmanizSeciliFuardaOrganizatorDegil")}</Alert>
      </Container></div>
    );
  }

  const kayitlar = veri?.records ?? [];
  const ozet = veri?.ozet;
  const kural = veri?.kural;
  const kuralVar = kural && kural.mod !== "NONE";
  const enBuyuk = enBuyukIstenen(kayitlar);
  const geri = `${konum.pathname}${konum.search}`;
  const satirYolu = (s) => talepSayfasiYolu(s.standId, geri, t("organizator.kwAsimi.sayfayaDon"));
  const toplamSayfa = Math.max(1, veri?.totalPages ?? 1);

  const ac = (s) => navigate(satirYolu(s));

  return (
    <div className="page-content">
      <Container fluid>
        <Card>
          <CardHeader className="d-flex align-items-center gap-2 flex-wrap">
            <Zap size={18} className="text-muted" />
            <h5 className="card-title mb-0 flex-grow-1">{t("organizator.serbestKwAsimi")}</h5>
            <Badge color="primary">{organizator.ad}</Badge>
            <Link to={TALEPLER_YOLU} className="btn btn-sm btn-light d-inline-flex align-items-center gap-1" data-testid="kw-asimi-geri">
              <ArrowLeft size={14} />{t("organizator.kwAsimi.taleplereDon")}
            </Link>
          </CardHeader>
          <CardBody>
            {hata?.durum === 403 && (
              <Alert color="info" className="small" data-testid="kw-asimi-kapali">{t("organizator.alanAcikDegil", { alan: t("organizator.taleplerVeBorc") })}</Alert>
            )}
            {hata && hata.durum !== 403 && <Alert color="danger" className="small" data-testid="kw-asimi-hata">{hata.mesaj}</Alert>}

            {!veri && yukleniyor && <div className="text-center py-4"><Spinner color="primary" /></div>}

            {kural && !kuralVar && (
              <Alert color="light" className="border small mb-0" data-testid="kw-asimi-kural-yok">{t("organizator.kuralYok")}</Alert>
            )}

            {kuralVar && ozet && (
              <>
                <KuralSatiri kural={kural} yerel={yerel} />

                <Row className="g-2 mb-2">
                  <Col xs={12} sm={4}>
                    <OzetKutusu testid="kutu-asan" renk="danger" etiket={t("organizator.kwAsimi.asanStand")} deger={ozet.asanStandSayisi}
                                secili={!durum.degerlendirilemeyen} onClick={() => degistir({ degerlendirilemeyen: false })} />
                  </Col>
                  <Col xs={12} sm={4}>
                    <OzetKutusu testid="kutu-toplam" renk="body" etiket={t("organizator.kwAsimi.toplamAsan")} deger={`${sayi(ozet.toplamAsanKw, yerel)} kW`} />
                  </Col>
                  <Col xs={12} sm={4}>
                    <OzetKutusu testid="kutu-degerlendirilemeyen" renk="secondary" etiket={t("organizator.degerlendirilemeyen")} deger={ozet.degerlendirilemeyenSayisi}
                                secili={durum.degerlendirilemeyen} onClick={() => degistir({ degerlendirilemeyen: true })} />
                  </Col>
                </Row>

                <Row className="g-2 mb-2 align-items-center">
                  <Col md={5}>
                    <Input bsSize="sm" placeholder={t("organizator.standFirmaAra")} value={girdi} data-testid="kw-asimi-ara" onChange={(e) => setGirdi(e.target.value)} />
                  </Col>
                  {salonlar.length > 0 && (
                    <Col md={3}>
                      <Input type="select" bsSize="sm" value={durum.salon ?? ""} aria-label={t("organizator.salon")} data-testid="kw-asimi-salon"
                             onChange={(e) => degistir({ salon: e.target.value ? Number(e.target.value) : null })}>
                        <option value="">{t("organizator.kwAsimi.tumSalonlar")}</option>
                        {salonlar.map((s) => <option key={s.salonId} value={s.salonId}>{s.salonName}</option>)}
                      </Input>
                    </Col>
                  )}
                  <Col className="d-flex justify-content-end">{yukleniyor && <Spinner size="sm" />}</Col>
                </Row>

                {veri && kayitlar.length === 0 && (
                  <div className="text-center text-muted py-3" data-testid="kw-asimi-bos">
                    {durum.degerlendirilemeyen ? t("organizator.kwAsimi.bosDegerlendirilemeyen") : t("organizator.kwAsimi.bos")}
                  </div>
                )}

                {kayitlar.length > 0 && (
                  <>
                    {/* tablet and up: the table */}
                    <div className="table-responsive d-none d-md-block">
                      <Table size="sm" className="align-middle mb-0" hover>
                        <thead className="table-light">
                          <tr>
                            <th>{t("organizator.stand")}</th><th>{t("organizator.salon")}</th><th>{t("organizator.katilimci")}</th><th>{t("organizator.dekorator")}</th>
                            <th className="text-end">{t("organizator.kwAsimi.krokiM2")}</th><th className="text-end">{t("organizator.serbestKw")}</th>
                            <th className="text-end">{t("organizator.istenenKw")}</th><th style={{ minWidth: 150 }}>{t("organizator.asanKw")}</th><th>{t("organizator.durum")}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {kayitlar.map((s) => (
                            <tr key={s.standId} role="link" style={{ cursor: "pointer" }} data-testid="kw-asimi-satir" onClick={() => ac(s)}>
                              <td className="fw-semibold">
                                <Link to={satirYolu(s)} onClick={(e) => e.stopPropagation()}>{s.standName}</Link>
                              </td>
                              <td className="small">{s.salonName}</td>
                              <td className="small">{s.katilimciFirmaName || "—"}</td>
                              <td className="small">{s.dekoratorFirmaName || "—"}</td>
                              <td className="text-end">{sayi(s.alanM2, yerel)}</td>
                              <td className="text-end">{sayi(s.serbestKw, yerel)}</td>
                              <td className="text-end">{sayi(s.istenenKw, yerel)}</td>
                              <td>
                                <div className="fw-semibold small">{s.asanKw === null || s.asanKw === undefined ? "—" : `+${sayi(s.asanKw, yerel)} kW`}</div>
                                <KwCubugu satir={s} enBuyuk={enBuyuk} />
                              </td>
                              <td><Badge color={durumRenk(s.status)} pill>{t(`organizator.talepFiltre.durumlar.${s.status}`, { defaultValue: s.statusLabel })}</Badge></td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>

                    {/* phone: one card per stand */}
                    <div className="list-group d-md-none" data-testid="kw-asimi-kartlar">
                      {kayitlar.map((s) => (
                        <Link key={s.standId} to={satirYolu(s)} className="list-group-item list-group-item-action">
                          <div className="d-flex justify-content-between align-items-start gap-2">
                            <div>
                              <div className="fw-semibold">{s.standName}</div>
                              <div className="small text-muted">{[s.salonName, s.katilimciFirmaName, s.dekoratorFirmaName].filter(Boolean).join(" · ")}</div>
                            </div>
                            <Badge color={durumRenk(s.status)} pill>{t(`organizator.talepFiltre.durumlar.${s.status}`, { defaultValue: s.statusLabel })}</Badge>
                          </div>
                          <div className="my-1"><KwCubugu satir={s} enBuyuk={enBuyuk} /></div>
                          <div className="d-flex flex-wrap gap-3 small">
                            <span>{t("organizator.kwAsimi.krokiM2")}: <strong>{sayi(s.alanM2, yerel)}</strong></span>
                            <span>{t("organizator.serbestKw")}: <strong>{sayi(s.serbestKw, yerel)}</strong></span>
                            <span>{t("organizator.istenenKw")}: <strong>{sayi(s.istenenKw, yerel)}</strong></span>
                            <span>{t("organizator.asanKw")}: <strong>{s.asanKw === null || s.asanKw === undefined ? "—" : `+${sayi(s.asanKw, yerel)}`}</strong></span>
                          </div>
                        </Link>
                      ))}
                    </div>

                    {toplamSayfa > 1 && (
                      <div className="d-flex align-items-center justify-content-between pt-2" data-testid="kw-asimi-sayfalama">
                        <span className="text-muted small">{t("organizator.sayfa", { v: durum.sayfa + 1 })}{" "}{toplamSayfa}</span>
                        <Pagination size="sm" className="mb-0">
                          <PaginationItem disabled={durum.sayfa === 0}><PaginationLink previous onClick={() => sayfayaGit(Math.max(0, durum.sayfa - 1))} /></PaginationItem>
                          <PaginationItem disabled={durum.sayfa + 1 >= toplamSayfa}><PaginationLink next onClick={() => sayfayaGit(Math.min(toplamSayfa - 1, durum.sayfa + 1))} /></PaginationItem>
                        </Pagination>
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </CardBody>
        </Card>
      </Container>
    </div>
  );
}
